import { useEffect, useState, useCallback, useRef } from "react";
import { Scanner } from "@yudiel/react-qr-scanner";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, AlertTriangle, Camera, Keyboard, RotateCcw, Ticket, Users } from "lucide-react";

export default function AdminCheckin() {
    const [stats, setStats] = useState({ total_orders: 0, total_seats: 0, checked_in_seats: 0, remaining: 0 });
    const [mode, setMode] = useState("camera"); // camera | manual
    const [scanning, setScanning] = useState(true);
    const [manualToken, setManualToken] = useState("");
    const [result, setResult] = useState(null); // {status, message, order}
    const [submitting, setSubmitting] = useState(false);
    const lastScanRef = useRef({ token: null, at: 0 });

    const loadStats = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/checkin/stats");
            setStats(data);
        } catch {}
    }, []);

    useEffect(() => { loadStats(); }, [loadStats]);

    const submitToken = async (token) => {
        const t = (token || "").trim();
        if (!t || submitting) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/checkin", { qr_token: t });
            setResult(data);
            setScanning(false);
            await loadStats();
            if (data.status === "ok") toast.success(data.message);
            else if (data.status === "already") toast.warning(data.message);
            else toast.error(data.message);
        } catch (err) {
            toast.error(formatErr(err));
        } finally { setSubmitting(false); }
    };

    const handleScan = useCallback((detected) => {
        if (!detected || !detected.length) return;
        const token = detected[0]?.rawValue;
        if (!token) return;
        const now = Date.now();
        // Debounce identical scans within 3s
        if (token === lastScanRef.current.token && now - lastScanRef.current.at < 3000) return;
        lastScanRef.current = { token, at: now };
        submitToken(token);
    }, [submitting]); // eslint-disable-line

    const reset = () => {
        setResult(null);
        setManualToken("");
        setScanning(true);
        lastScanRef.current = { token: null, at: 0 };
    };

    return (
        <div data-testid="admin-checkin">
            <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
                <div>
                    <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-2">Game Night</div>
                    <h1 className="h-display text-4xl">Gate Check-in</h1>
                </div>
                <button onClick={reset} className="btn-ghost-gold px-4 py-2 text-xs inline-flex items-center gap-2" data-testid="checkin-reset">
                    <RotateCcw size={14} /> Reset Scanner
                </button>
            </div>

            {/* Stats bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                <Stat label="Orders" value={stats.total_orders} icon={Ticket} testid="stat-orders" />
                <Stat label="Total Seats" value={stats.total_seats} icon={Users} testid="stat-seats" />
                <Stat label="Checked In" value={stats.checked_in_seats} icon={CheckCircle2} highlight="green" testid="stat-in" />
                <Stat label="Remaining" value={stats.remaining} icon={AlertTriangle} highlight="gold" testid="stat-remaining" />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Scanner / Manual */}
                <div className="card-flat p-6">
                    <div className="flex gap-2 mb-5">
                        <button
                            onClick={() => setMode("camera")}
                            data-testid="mode-camera"
                            className={`px-4 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-2 ${mode === "camera" ? "bg-gold text-black" : "border border-line text-ink hover:text-gold"}`}
                        >
                            <Camera size={12} /> Camera
                        </button>
                        <button
                            onClick={() => setMode("manual")}
                            data-testid="mode-manual"
                            className={`px-4 py-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-2 ${mode === "manual" ? "bg-gold text-black" : "border border-line text-ink hover:text-gold"}`}
                        >
                            <Keyboard size={12} /> Manual
                        </button>
                    </div>

                    {mode === "camera" ? (
                        <div className="aspect-square w-full bg-black overflow-hidden relative" data-testid="scanner-frame">
                            {scanning ? (
                                <Scanner
                                    onScan={handleScan}
                                    onError={() => {}}
                                    constraints={{ facingMode: "environment" }}
                                    styles={{ container: { width: "100%", height: "100%" } }}
                                    components={{ audio: false, finder: true }}
                                />
                            ) : (
                                <div className="absolute inset-0 grid place-items-center bg-bg-soft">
                                    <button onClick={reset} className="btn-gold px-7 py-4 text-xs">
                                        Scan Next
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div data-testid="manual-form">
                            <label className="text-[10px] tracking-widest uppercase text-ink font-bold">Paste QR Token</label>
                            <input
                                value={manualToken}
                                onChange={(e) => setManualToken(e.target.value)}
                                placeholder="e.g. K9pX4mZ_..."
                                data-testid="manual-token"
                                className="w-full mt-2 px-4 py-3 text-sm font-mono"
                            />
                            <button
                                onClick={() => submitToken(manualToken)}
                                disabled={submitting || !manualToken}
                                data-testid="manual-submit"
                                className="btn-gold px-7 py-4 text-xs mt-3 disabled:opacity-50"
                            >
                                {submitting ? "Checking…" : "Check In"}
                            </button>
                        </div>
                    )}
                </div>

                {/* Result */}
                <div className="card-flat p-6 min-h-[260px]" data-testid="checkin-result">
                    {!result ? (
                        <div className="text-ink text-sm h-full grid place-items-center text-center">
                            <div>
                                <Camera className="mx-auto opacity-30" size={48} />
                                <div className="mt-4 tracking-widest uppercase text-xs">Awaiting scan…</div>
                            </div>
                        </div>
                    ) : (
                        <ResultCard result={result} />
                    )}
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, icon: Icon, highlight, testid }) {
    const tone = highlight === "green" ? "text-green-400" : highlight === "gold" ? "text-gold" : "text-white";
    return (
        <div className="card-flat p-4" data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-ink">{label}</div>
                <Icon size={14} className="text-ink" />
            </div>
            <div className={`h-display text-3xl mt-2 ${tone}`}>{value}</div>
        </div>
    );
}

function ResultCard({ result }) {
    const { status, message, order } = result;
    const palette =
        status === "ok" ? { icon: CheckCircle2, color: "text-green-400", title: "VALID" } :
        status === "already" ? { icon: AlertTriangle, color: "text-gold", title: "ALREADY IN" } :
        { icon: XCircle, color: "text-red-400", title: "REJECTED" };
    const Icon = palette.icon;

    return (
        <div>
            <div className={`${palette.color} flex items-center gap-3`}>
                <Icon size={36} />
                <div className="h-display text-3xl">{palette.title}</div>
            </div>
            <div className="text-white/90 mt-2 text-sm">{message}</div>
            {order && (
                <div className="mt-5 border-t border-line pt-5 text-sm space-y-2">
                    <Row label="Buyer" value={order.buyer_name} />
                    <Row label="Email" value={order.buyer_email} />
                    <Row label="Tier" value={`${order.tier_name}${order.is_season_pass ? " · Season" : ""}`} />
                    <Row label="Quantity" value={`${order.checked_in_count || 0} / ${order.quantity}`} highlight={status === "ok"} />
                    <Row label="Order" value={order.id.slice(0, 8).toUpperCase()} mono />
                </div>
            )}
        </div>
    );
}

function Row({ label, value, mono, highlight }) {
    return (
        <div className="flex justify-between">
            <div className="text-[10px] tracking-widest uppercase text-ink">{label}</div>
            <div className={`${mono ? "font-mono" : ""} ${highlight ? "text-gold h-display text-base" : ""}`}>{value}</div>
        </div>
    );
}
